/**
 * ShiftHistoryPage
 * CCH Care — Phase 5f
 *
 * Paginated list of past shifts with ADL completion stats.
 * Filterable by date range and unit.
 * Tap a shift to view ShiftDetailPage.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../config/constants';
import client from '../../api/client';

const BASE = '/shifts/history.php';

function localDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function defaultDates() {
  const to   = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  return { from: fmt(from), to: fmt(to) };
}

function completionColor(rate) {
  if (rate >= 90) return COLORS.accent;
  if (rate >= 70) return COLORS.warning;
  return COLORS.danger;
}

function formatShiftDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const today     = new Date();
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const todayStr     = localDate();
  const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth()+1).padStart(2,'0')}-${String(yesterday.getDate()).padStart(2,'0')}`;
  if (dateStr === todayStr)     return 'Today';
  if (dateStr === yesterdayStr) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function ShiftHistoryPage() {
  const navigate = useNavigate();
  const [shifts, setShifts]     = useState([]);
  const [units, setUnits]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [dates, setDates]       = useState(defaultDates);
  const [unitFilter, setUnitFilter] = useState('');
  const [page, setPage]         = useState(1);
  const [pagination, setPagination] = useState(null);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        action:    'list',
        page:      pg,
        limit:     20,
        date_from: dates.from,
        date_to:   dates.to,
      });
      if (unitFilter) params.set('unit_id', unitFilter);

      const res = await client.get(`${BASE}?${params}`);
      if (res.data.success) {
        setShifts(res.data.shifts || []);
        setUnits(res.data.units || []);
        setPagination(res.data.pagination || null);
        setPage(pg);
      } else {
        setError(res.data.error || 'Failed to load');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [dates, unitFilter]);

  useEffect(() => { load(1); }, [load]);

  function setPreset(days) {
    const to   = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    setDates({ from: fmt(from), to: fmt(to) });
  }

  // Group shifts by date
  const grouped = shifts.reduce((acc, s) => {
    const key = s.shift_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* Header */}
      <div style={{
        padding: '12px 16px', background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <button
            onClick={() => navigate('/dashboard/more')}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: COLORS.primary, padding: '2px 6px' }}
          >
            ←
          </button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>Shift History</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Past shifts and ADL documentation</div>
          </div>
        </div>

        {/* Date filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          <input
            type="date"
            value={dates.from}
            onChange={e => setDates(prev => ({ ...prev, from: e.target.value }))}
            style={inputStyle}
          />
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>→</span>
          <input
            type="date"
            value={dates.to}
            onChange={e => setDates(prev => ({ ...prev, to: e.target.value }))}
            style={inputStyle}
          />
          {units.length > 1 && (
            <select
              value={unitFilter}
              onChange={e => setUnitFilter(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">All units</option>
              {units.map(u => (
                <option key={u.unit_id} value={u.unit_id}>{u.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Presets */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { label: '7 days',  days: 6  },
            { label: '30 days', days: 29 },
            { label: '90 days', days: 89 },
          ].map(p => (
            <button key={p.label} onClick={() => setPreset(p.days)} style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: 'none', color: COLORS.textMuted,
              border: `1px solid ${COLORS.border}`, cursor: 'pointer',
            }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
            Loading shifts…
          </div>
        )}

        {error && !loading && (
          <div style={{
            background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
            borderRadius: 10, padding: '12px 16px', color: COLORS.danger, fontSize: 13, marginBottom: 12,
          }}>
            ⚠️ {error}
          </div>
        )}

        {!loading && !error && shifts.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, color: COLORS.textMuted }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>No shifts found</div>
            <div style={{ fontSize: 13 }}>Try adjusting the date range or unit filter.</div>
          </div>
        )}

        {!loading && !error && Object.keys(grouped).map(dateKey => (
          <div key={dateKey} style={{ marginBottom: 20 }}>
            {/* Date header */}
            <div style={{
              fontSize: 12, fontWeight: 700, color: COLORS.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              marginBottom: 8, paddingBottom: 4,
              borderBottom: `1px solid ${COLORS.border}`,
            }}>
              {formatShiftDate(dateKey)}
              <span style={{ fontWeight: 400, marginLeft: 6 }}>
                {new Date(dateKey + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>

            {grouped[dateKey].map(shift => {
              const rate = shift.completion_rate;
              return (
                <div
                  key={shift.care_shift_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/dashboard/shift-history/${shift.care_shift_id}`)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/dashboard/shift-history/${shift.care_shift_id}`); }}
                  style={{
                    background: COLORS.surface, borderRadius: 10,
                    border: `1px solid ${COLORS.border}`,
                    padding: '12px 14px', marginBottom: 8,
                    display: 'flex', alignItems: 'center', gap: 12,
                    cursor: 'pointer',
                  }}
                >
                  {/* Completion ring */}
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    background: `conic-gradient(${completionColor(rate)} ${rate * 3.6}deg, ${COLORS.border} 0deg)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: COLORS.surface,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800, color: completionColor(rate),
                    }}>
                      {rate}%
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 2 }}>
                      {shift.shift_name}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                      {shift.unit_name}
                      {shift.start_time && (
                        <span style={{ marginLeft: 6 }}>
                          · {shift.start_time.slice(0,5)}
                          {shift.end_time ? `–${shift.end_time.slice(0,5)}` : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.accent }}>{shift.completed_count}</div>
                      <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase' }}>Done</div>
                    </div>
                    {shift.partial_count > 0 && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.warning }}>{shift.partial_count}</div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase' }}>Partial</div>
                      </div>
                    )}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.textMuted }}>{shift.resident_count}</div>
                      <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase' }}>Total</div>
                    </div>
                  </div>

                  <span style={{ color: COLORS.textMuted, fontSize: 16, flexShrink: 0 }}>›</span>
                </div>
              );
            })}
          </div>
        ))}

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && !loading && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
            <button
              onClick={() => load(page - 1)}
              disabled={page <= 1}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13, cursor: page > 1 ? 'pointer' : 'not-allowed',
                background: page > 1 ? COLORS.primary : COLORS.border,
                color: page > 1 ? '#fff' : COLORS.textMuted, border: 'none', fontWeight: 600,
              }}
            >
              ← Previous
            </button>
            <span style={{ padding: '8px 12px', fontSize: 13, color: COLORS.textMuted }}>
              {page} / {pagination.total_pages}
            </span>
            <button
              onClick={() => load(page + 1)}
              disabled={page >= pagination.total_pages}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13,
                cursor: page < pagination.total_pages ? 'pointer' : 'not-allowed',
                background: page < pagination.total_pages ? COLORS.primary : COLORS.border,
                color: page < pagination.total_pages ? '#fff' : COLORS.textMuted, border: 'none', fontWeight: 600,
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '6px 10px', borderRadius: 6,
  border: `1px solid ${COLORS.border}`, fontSize: 12,
  color: COLORS.text, background: COLORS.surface,
  fontFamily: 'inherit',
};
